// Impostazioni → Estrattore → Ricontrolli delle candidate (solo admin).
//
// Un interruttore a tre stati per il lavoro «ricontrolla_candidata»: rileggere sui portali le candidate della
// ricerca, per sapere se sono ancora online, se il prezzo è cambiato e dov'è la sede dell'agenzia.
//
// Sta nel database e non in una variabile d'ambiente perché cambiarlo non deve richiedere un rilascio: il
// server lo rilegge a ogni richiesta dell'estensione. Si parte da «spento» di proposito — apre pagine sui
// portali, e quando si accende è perché qualcuno l'ha deciso.
//
// Lo stesso stato si vede in sola lettura in Impostazioni → Lavori automatici.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { errore } from '../scheda/comuni';

const STATI = [
  ['spento', 'Spento', 'Nessun ricontrollo parte. È lo stato di partenza.'],
  ['prova', 'Prova', 'Al massimo 5 ricontrolli al giorno in tutto, e solo delle case che lo staff ha davanti '
    + 'adesso. Serve a vedere se il meccanismo funziona prima di accenderlo davvero.'],
  ['acceso', 'Acceso', 'Regole complete: le case in vista ogni 3 giorni, il gruppo 1 proponibile e il gruppo 2 '
    + 'ogni 7. Mai più dei tetti per portale, e mai più del 30% del limite giornaliero dell’estensione.'],
];

const RicontrolliCandidate = () => {
  const [dati, setDati] = useState(null);
  const [problema, setProblema] = useState(false);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/ricontrolli-candidate`).then((r) => setDati(r.data)).catch(() => setProblema(true));
  }, []);

  const cambia = async (stato) => {
    setSalvo(true);
    try {
      const r = await axios.put(`${API}/admin/ricontrolli-candidate`, { stato });
      setDati(r.data);
      toast.success(`Ricontrolli delle candidate: ${stato}`);
    } catch (e) { errore(e); } finally { setSalvo(false); }
  };

  if (problema) {
    return (
      <section className="card" data-testid="ricontrolli-candidate-problema">
        <div className="ttl"><h2>Ricontrolli delle candidate</h2></div>
        <p className="imp-nota">Lo stato non è arrivato dal server: ricaricate la pagina fra poco.</p>
      </section>
    );
  }
  if (!dati) return null;

  const tetti = Object.entries(dati.tetti_portale || {});
  return (
    <section className="card" data-testid="ricontrolli-candidate">
      <div className="ttl">
        <h2>Ricontrolli delle candidate <small>è ancora online? il prezzo è cambiato?</small></h2>
      </div>
      <p className="imp-nota">
        Rilegge sui portali le candidate che contano, con l’estensione dell’ufficio. Serve l’estensione{' '}
        {dati.versione_minima} o successiva: con una più vecchia il lavoro non viene nemmeno offerto.
      </p>

      <div className="imp-scelte" role="radiogroup" aria-label="Ricontrolli delle candidate">
        {STATI.map(([valore, nome, spiegazione]) => (
          <label key={valore} className={dati.stato === valore ? 'scelta scelta-attiva' : 'scelta'}>
            <input
              type="radio"
              name="ricontrolli-candidate"
              value={valore}
              checked={dati.stato === valore}
              disabled={salvo}
              onChange={() => cambia(valore)}
            />
            <span><b>{nome}</b><small>{spiegazione}</small></span>
          </label>
        ))}
      </div>

      <div className="kv" style={{ marginTop: '.8rem' }}>
        <div>
          <small>Fatti oggi</small>
          <b data-testid="ricontrolli-fatti-oggi">{dati.fatti_oggi ?? 0}</b>
        </div>
        <div>
          <small>Tetti al giorno per portale</small>
          <b>{tetti.map(([p, n]) => `${p} ${n}`).join(' · ') || '—'}</b>
        </div>
      </div>
      <p className="imp-nota" style={{ marginTop: '.6rem' }}>
        Il tetto vero è il minore fra la somma dei tetti qui sopra e il {Math.round((dati.quota_del_limite || 0) * 100)}%
        del limite giornaliero impostato nell’estensione: così le ricerche per i clienti non restano a secco.
      </p>
    </section>
  );
};

// Nella stessa scheda, l'interruttore dell'archivio di ogni mattina: due stati, non tre, e l'esito dell'ultimo
// giro. Sta qui perché è l'altro lavoro automatico che tocca le candidate, e chi li guarda li guarda insieme.
export const ArchivioAutomatico = () => {
  const [dati, setDati] = useState(null);
  const [problema, setProblema] = useState(false);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/archivio-automatico`).then((r) => setDati(r.data)).catch(() => setProblema(true));
  }, []);

  const cambia = async (stato) => {
    setSalvo(true);
    try {
      const r = await axios.put(`${API}/admin/archivio-automatico`, { stato });
      setDati(r.data);
      toast.success(`Archivio automatico: ${stato}`);
    } catch (e) { errore(e); } finally { setSalvo(false); }
  };

  if (problema) {
    return (
      <section className="card" data-testid="archivio-automatico-problema">
        <div className="ttl"><h2>Archivio automatico</h2></div>
        <p className="imp-nota">Lo stato non è arrivato dal server: ricaricate la pagina fra poco.</p>
      </section>
    );
  }
  if (!dati) return null;
  const giro = dati.ultimo_giro;

  return (
    <section className="card" data-testid="archivio-automatico">
      <div className="ttl">
        <h2>Archivio automatico <small>le candidate non guardate da {dati.giorni} giorni</small></h2>
      </div>
      <p className="imp-nota">
        Ogni mattina alle 8:30 mette in archivio le candidate che nessuno ha guardato né toccato da{' '}
        {dati.giorni} giorni. Non cancella niente: si ripristinano da «mostra archiviate». Al massimo{' '}
        {dati.massimo_per_giro} per giro, le più vecchie per prime; non parte se il backup ha più di{' '}
        {dati.ore_backup_massime} ore, e non archivia niente se toccherebbe più del{' '}
        {Math.round(dati.proporzione_massima * 100)}% delle candidate aperte.
      </p>

      <div className="imp-scelte" role="radiogroup" aria-label="Archivio automatico">
        {[['spento', 'Spento', 'Il giro non parte. È lo stato di partenza.'],
          ['acceso', 'Acceso', 'Il giro parte ogni mattina alle 8:30.']].map(([valore, nome, spiegazione]) => (
            <label key={valore} className={dati.stato === valore ? 'scelta scelta-attiva' : 'scelta'}>
              <input type="radio" name="archivio-automatico" value={valore} checked={dati.stato === valore}
                     disabled={salvo} onChange={() => cambia(valore)} />
              <span><b>{nome}</b><small>{spiegazione}</small></span>
            </label>
        ))}
      </div>

      <div className="kv" style={{ marginTop: '.8rem' }}>
        <div>
          <small>Ultimo giro</small>
          <b data-testid="archivio-ultimo-giro">{giro?.quando ? new Date(giro.quando).toLocaleString('it-IT') : 'mai'}</b>
        </div>
        <div>
          <small>Archiviate</small>
          <b>{giro ? (giro.archiviate ?? 0) : '—'}</b>
        </div>
      </div>
      {giro?.fermo && (
        <p className="imp-nota w" data-testid="archivio-fermo" style={{ marginTop: '.6rem' }}>{giro.fermo}</p>
      )}
    </section>
  );
};

export default RicontrolliCandidate;
